import JSZip from 'jszip';
// Use require to keep the parser's newer declaration syntax out of TS 3.7.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const { kml } = require('@tmcw/togeojson');

export interface RouteLayer {
  id: number;
  ready: Promise<void>;
  setMap(map: any): void;
}

export async function readKml(buffer: ArrayBuffer): Promise<Document> {
  const bytes = new Uint8Array(buffer);
  let xml: string;
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
    const archive = await JSZip.loadAsync(buffer);
    const names = Object.keys(archive.files).filter(name => /\.kml$/i.test(name));
    const name = names.find(name => /(^|\/)doc\.kml$/i.test(name)) || names[0];
    if (!name) throw new Error('KMZ does not contain a KML document');
    xml = await archive.files[name].async('string');
  } else {
    xml = new TextDecoder('utf-8').decode(buffer);
  }
  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (document.getElementsByTagName('parsererror').length) {
    throw new Error('Invalid KML XML');
  }
  return document;
}

// Remap only the file origin; keep the path and query returned by either API version.
export function getRouteUrl(url: string): string {
  const base = process.env.VUE_APP_KML_BASE_URL;
  if (!base) return url;
  const source = new URL(url, window.location.href);
  return new URL(source.pathname + source.search, new URL(base, window.location.href)).href;
}

export function createRouteLayer(google: any, map: any, config: any, fitBounds: boolean): RouteLayer {
  const data = new google.maps.Data();
  let currentMap = config.isSelected === false ? null : map;
  data.setMap(currentMap);
  data.setStyle(feature => ({
    clickable: false,
    strokeColor: feature.getProperty('stroke') || config.color || '#0066cc',
    strokeOpacity: feature.getProperty('stroke-opacity') == null ? 1 : Number(feature.getProperty('stroke-opacity')),
    strokeWeight: Number(feature.getProperty('stroke-width')) || 3,
    fillColor: feature.getProperty('fill') || config.color || '#0066cc',
    fillOpacity: feature.getProperty('fill-opacity') == null ? 0.25 : Number(feature.getProperty('fill-opacity')),
  }));
  const layer: RouteLayer = {
    id: config.id,
    setMap(nextMap) {
      currentMap = nextMap;
      data.setMap(nextMap);
    },
    ready: null,
  };
  layer.ready = (async () => {
    const response = await fetch(getRouteUrl(config.url));
    if (!response.ok) throw new Error('KML HTTP ' + response.status);
    const document = await readKml(await response.arrayBuffer());
    const geojson = kml(document, { skipNullGeometry: true });
    if (!geojson.features.length) throw new Error('KML has no supported geometries');
    const features = data.addGeoJson(geojson);
    if (fitBounds && currentMap) {
      const bounds = new google.maps.LatLngBounds();
      for (const feature of features) {
        feature.getGeometry().forEachLatLng(point => bounds.extend(point));
      }
      if (!bounds.isEmpty()) currentMap.fitBounds(bounds);
    }
  })();
  return layer;
}
